import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getToken } from '@/core/services/JwtService'
import { logError } from '@/utils/safeErrorLog'

/** Where a 401 sends the patient; the flag lets the login page explain why they landed there. */
export const SESSION_EXPIRED_URL = '/account/login?sessionExpired=1'

/**
 * Attaches the bearer token to every request, and signs the patient out on a 401.
 *
 * On a 401 the whole of sessionStorage is cleared (token, profile, brand override) and the browser
 * does a full load of the login page, so no in-memory state from the old session survives.
 */
export function attachApiAxiosInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken()
      if (token && config.headers) {
        config.headers.Authorization = `bearer ${token}`
      }
      return config
    },
    (error: AxiosError) => {
      logError('Request Error:', error)
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response) {
        if (error.response.status === 401) {
          sessionStorage.clear()
          window.location.href = SESSION_EXPIRED_URL
        } else {
          logError(`Response Error [${error.response.status}]:`, error)
        }
      } else if (error.request) {
        logError('No Response Received:', error)
      } else {
        logError('Error in setup:', error)
      }
      return Promise.reject(error)
    }
  )
}
